/**
 * SkillDelivery Platform
 * Early Warnings Component - AI-powered project alerts
 *
 * Conference Sprint S2d: Intelligent warning system for EVM
 */

import React from 'react';
import { AlertTriangle, AlertCircle, Lightbulb, TrendingDown, Clock, DollarSign } from 'lucide-react';
import './EarlyWarnings.css';

const EarlyWarnings = ({ metrics, budget, workPackages }) => {
  // Generate warnings based on metrics
  const generateWarnings = () => {
    const warnings = [];

    // Schedule warnings
    if (metrics.spi < 0.9) {
      warnings.push({
        type: 'critical',
        icon: <Clock />,
        title: 'Critical Schedule Delay',
        message: `Project is ${((1 - metrics.spi) * 100).toFixed(1)}% behind schedule. SPI of ${metrics.spi.toFixed(2)} indicates significant delays.`,
        recommendation: 'Consider adding resources or fast-tracking critical path activities.'
      });
    } else if (metrics.spi < 1.0) {
      warnings.push({
        type: 'warning',
        icon: <Clock />,
        title: 'Schedule Variance Alert',
        message: `Schedule Variance of ₹${Math.abs(metrics.sv).toFixed(2)}L (${((1 - metrics.spi) * 100).toFixed(1)}% behind)`,
        recommendation: 'Monitor closely and identify activities causing delays.'
      });
    }

    // Cost warnings
    if (metrics.cpi < 0.9) {
      warnings.push({
        type: 'critical',
        icon: <DollarSign />,
        title: 'Critical Cost Overrun',
        message: `Project is ${((1 - metrics.cpi) * 100).toFixed(1)}% over budget. CPI of ${metrics.cpi.toFixed(2)} indicates cost control issues.`,
        recommendation: 'Immediate cost review required. Consider value engineering options.'
      });
    } else if (metrics.cpi < 1.0) {
      warnings.push({
        type: 'warning',
        icon: <DollarSign />,
        title: 'Cost Variance Alert',
        message: `Cost Variance of ₹${Math.abs(metrics.cv).toFixed(2)}L (${((1 - metrics.cpi) * 100).toFixed(1)}% over budget)`,
        recommendation: 'Review material costs and labor productivity.'
      });
    }

    // Completion forecast warning
    if (metrics.eac > budget.total * 1.1) {
      warnings.push({
        type: 'critical',
        icon: <TrendingDown />,
        title: 'Budget Overrun Forecast',
        message: `At current rate, project will cost ₹${metrics.eac.toFixed(2)}L — ₹${Math.abs(metrics.vac).toFixed(2)}L over the ₹${budget.total}L budget.`,
        recommendation: 'Urgent intervention needed to improve CPI or reduce scope.'
      });
    }

    // TCPI warning
    if (metrics.tcpi > 1.1) {
      warnings.push({
        type: 'warning',
        icon: <AlertTriangle />,
        title: 'Recovery Performance Required',
        message: `TCPI of ${metrics.tcpi.toFixed(2)} means remaining work must be done ${((metrics.tcpi - 1) * 100).toFixed(0)}% more efficiently than planned.`,
        recommendation: metrics.tcpi > 1.2
          ? 'Target may be unrealistic. Consider budget revision or scope reduction.'
          : 'Achievable with focused effort. Implement corrective actions immediately.'
      });
    }

    // Work package specific warnings
    const behindPackages = workPackages.filter(wp => wp.status === 'behind');
    if (behindPackages.length > 0) {
      warnings.push({
        type: 'warning',
        icon: <AlertCircle />,
        title: 'Work Packages Behind Schedule',
        message: `${behindPackages.length} work package(s) are behind: ${behindPackages.map(wp => wp.name).join(', ')}`,
        recommendation: 'Focus resources on delayed packages. Review dependencies and constraints.'
      });
    }

    // If no warnings, show positive status
    if (warnings.length === 0) {
      warnings.push({
        type: 'success',
        icon: <Lightbulb />,
        title: 'Project On Track',
        message: 'All performance indices are within acceptable ranges.',
        recommendation: 'Continue monitoring and maintain current performance levels.'
      });
    }

    return warnings;
  };

  const warnings = generateWarnings();

  return (
    <section className="early-warnings">
      <h2>
        <AlertTriangle size={20} />
        Early Warning System
      </h2>

      <div className="warnings-list">
        {warnings.map((warning, index) => (
          <div key={index} className={`warning-card ${warning.type}`}>
            <div className="warning-icon">
              {warning.icon}
            </div>
            <div className="warning-content">
              <h4>{warning.title}</h4>
              <p className="warning-message">{warning.message}</p>
              <div className="warning-recommendation">
                <Lightbulb size={14} />
                <span>{warning.recommendation}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="warnings-footer">
        <p>💡 Powered by SkillDelivery Civil Construction AI</p>
      </div>
    </section>
  );
};

export default EarlyWarnings;
